import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Profile types
export interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  phone_number: string | null;
  professional_title: string | null;
  bio: string | null;
  business_hours: {
    [key: string]: { start: string; end: string; } | null;
  } | null;
  service_categories: string[] | null;
  pricing_info: Record<string, any> | null;
  profile_image_url: string | null;
  portfolio_images: string[] | null;
  years_experience: number | null;
  certifications: string[] | null;
  service_area: string | null;
  social_media: Record<string, string> | null;
  profile_completion: number;
  is_draft: boolean;
  created_at: string;
  updated_at: string;
}

// Settings types
export interface UserSettings {
  user_id: string;
  cache_duration: string;
  realtime_enabled: boolean;
  sync_frequency: string;
  offline_mode: boolean;
  created_at: string;
  updated_at: string;
}

// Profile operations
export const profileOperations = {
  async getProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateProfile(userId: string, updates: Partial<Profile>) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  subscribeToProfile(userId: string, callback: (profile: Profile) => void) {
    return supabase
      .from('profiles')
      .on('UPDATE', payload => {
        if (payload.new.id === userId) {
          callback(payload.new as Profile);
        }
      })
      .subscribe();
  }
};

// Settings operations
export const settingsOperations = {
  async getSettings(userId: string) {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateSettings(userId: string, updates: Partial<UserSettings>) {
    const { data, error } = await supabase
      .from('user_settings')
      .update(updates)
      .eq('user_id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  subscribeToSettings(userId: string, callback: (settings: UserSettings) => void) {
    return supabase
      .from('user_settings')
      .on('UPDATE', payload => {
        if (payload.new.user_id === userId) {
          callback(payload.new as UserSettings);
        }
      })
      .subscribe();
  }
};